import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { Badge } from 'src/components/ui/Badge';
import { ACTION_THRESHOLD, WATCH_FLOOR, WATCH_SAMPLE_SOURCE_LABEL, WATCH_STATUS_LABEL, WATCH_STATUS_MEANING } from 'src/lib/relevance';
import { formatDateTime } from 'src/lib/time';
import type { EditorialRelevanceSample, EditorialRelevanceWatch } from 'src/types/editorial';

/**
 * What one story's relevance did after it was first measured.
 *
 * Composition §6 — never a bare score, and this is the screen where that rule finally has
 * somewhere to point: the X review's 42 was a reading taken at one moment, and until now nothing
 * said whether it was on its way to 71 or to 8.
 *
 * Inline SVG, no charting dependency, for the reason the coverage sparkline gives: this is a
 * polyline and two rules, and the smallest chart library in common use is larger than the screen
 * it would draw on.
 *
 * The two reference lines are the argument of the whole feature. 65 is where the pipeline acts —
 * a curve that crosses it and falls back is a story that was briefly worth drafting and is not
 * any more, which is a fact no single score can carry. 10 is where tracking starts, and a curve
 * that touches it is a subject nobody is talking about.
 *
 * Points sit at their observation time, not at their index, so an hour of silence between two
 * readings looks like an hour. Every relevance element here carries the `social` tone and never a
 * verification tone: this is attention, not truth (Design System §3.2).
 */
export function RelevanceCurve({ watch }: { watch: EditorialRelevanceWatch }) {
  const samples = watch.samples;
  if (samples.length === 0) {
    return <AbsenceState kind="empty" what="El seguimiento se abrió pero no tiene lecturas." />;
  }

  return (
    <figure className="watch">
      <figcaption className="watch__head">
        <Badge tone="social" title={WATCH_STATUS_MEANING[watch.status]}>
          {WATCH_STATUS_LABEL[watch.status]}
        </Badge>
        <span className="watch__span">
          {formatDateTime(watch.openedAt)} → {formatDateTime(watch.closedAt ?? watch.expiresAt)}
        </span>
        <span className="watch__figures mono">
          {samples.length === 1 ? '1 lectura' : `${samples.length} lecturas`} · inicial {Math.round(watch.initialScore)} · pico{' '}
          {Math.round(watch.peakScore)} · última {Math.round(watch.latestScore)}
        </span>
      </figcaption>

      <WatchPlot watch={watch} />

      {watch.closedReason && <p className="watch__reason">{watch.closedReason}</p>}
      {!watch.closedReason && watch.status === 'watching' && watch.nextSampleAt && (
        <p className="watch__reason">Siguiente relectura después de {formatDateTime(watch.nextSampleAt)}, si la caja está capturando.</p>
      )}

      <ol className="watch__samples">
        {samples.map((sample, index) => (
          <li key={`${sample.observedAt ?? index}-${index}`} className="watch__sample">
            <span className="watch__sampleTime">{formatDateTime(sample.observedAt)}</span>
            <span className="watch__sampleScore mono" data-crossed={sample.score >= ACTION_THRESHOLD || undefined}>
              {Math.round(sample.score)}
            </span>
            <span className="watch__sampleSource">{WATCH_SAMPLE_SOURCE_LABEL[sample.source] ?? sample.source}</span>
            {/* A score that rose while the post count stayed at two is a re-weighting, not a
                growing conversation — so the evidence behind each reading stays on the row. */}
            <span className="watch__samplePosts mono">{sample.postsSeen > 0 ? `${sample.postsSeen} pubs.` : '—'}</span>
          </li>
        ))}
      </ol>
    </figure>
  );
}

const PLOT_WIDTH = 100;
const PLOT_HEIGHT = 46;

function WatchPlot({ watch }: { watch: EditorialRelevanceWatch }) {
  const points = plotPoints(watch);
  const yFor = (score: number) => PLOT_HEIGHT - (Math.max(0, Math.min(score, 100)) / 100) * PLOT_HEIGHT;

  return (
    <svg className="watch__plot" viewBox={`0 0 ${PLOT_WIDTH} ${PLOT_HEIGHT}`} preserveAspectRatio="none" role="img" aria-label={plotDescription(watch)}>
      <line className="watch__rule watch__rule--action" x1="0" x2={PLOT_WIDTH} y1={yFor(ACTION_THRESHOLD)} y2={yFor(ACTION_THRESHOLD)} />
      <line className="watch__rule watch__rule--floor" x1="0" x2={PLOT_WIDTH} y1={yFor(WATCH_FLOOR)} y2={yFor(WATCH_FLOOR)} />

      {points.length > 1 && <polyline className="watch__line" points={points.map(point => `${point.x},${yFor(point.sample.score)}`).join(' ')} />}

      {points.map((point, index) => (
        <circle
          key={`${point.sample.observedAt ?? index}-${index}`}
          className="watch__point"
          cx={point.x}
          cy={yFor(point.sample.score)}
          r={1.6}
          data-crossed={point.sample.score >= ACTION_THRESHOLD || undefined}
        >
          <title>{`${formatDateTime(point.sample.observedAt)}: ${Math.round(point.sample.score)}`}</title>
        </circle>
      ))}
    </svg>
  );
}

type PlotPoint = { x: number; sample: EditorialRelevanceSample };

/**
 * Each reading at its own time along the window.
 *
 * The window is the opening reading through the close — or through the last reading when the watch
 * is still open, so a curve two readings into a five-hour window fills the plot instead of hugging
 * the left edge. Readings with no timestamp fall back to their position, which only happens on
 * documents written before the field existed.
 */
function plotPoints(watch: EditorialRelevanceWatch): PlotPoint[] {
  const samples = watch.samples;
  const times = samples.map(sample => (sample.observedAt ? Date.parse(sample.observedAt) : Number.NaN));
  const known = times.filter(time => Number.isFinite(time));

  if (known.length < 2) {
    const step = samples.length > 1 ? PLOT_WIDTH / (samples.length - 1) : 0;
    return samples.map((sample, index) => ({ x: index * step, sample }));
  }

  const first = Math.min(...known);
  const last = Math.max(...known);
  const span = last - first || 1;

  return samples.map((sample, index) => {
    const time = times[index];
    const x = Number.isFinite(time) ? ((time! - first) / span) * PLOT_WIDTH : (index / Math.max(samples.length - 1, 1)) * PLOT_WIDTH;
    return { x, sample };
  });
}

/** The curve in words, for a reader who is not looking at it. */
function plotDescription(watch: EditorialRelevanceWatch): string {
  return `Relevancia de ${Math.round(watch.initialScore)} a ${Math.round(watch.latestScore)}, pico ${Math.round(watch.peakScore)}, en ${watch.samples.length} lecturas`;
}
